import React from "react";
import { format, isSameMonth, isToday, startOfMonth, getDay, addDays } from "date-fns";
import { cn } from "@/lib/utils";
import DayCell from "./DayCell";

interface CalendarGridProps {
  currentDate: Date;
  view: string;
  events: any[];
  getDaysToDisplay: () => Date[];
  onEventClick?: (event: any) => void;
}

const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentDate,
  view,
  events,
  getDaysToDisplay,
  onEventClick,
}) => {
  // Get events for a specific day
  const getEventsForDay = (day: Date) => {
    return events.filter(
      (event) =>
        event.date.getDate() === day.getDate() &&
        event.date.getMonth() === day.getMonth() &&
        event.date.getFullYear() === day.getFullYear()
    );
  };

  // Generate header row based on view
  const renderHeaderRow = () => {
    return (
      <div className="grid grid-cols-7 bg-gray-100 font-semibold">
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
          <div key={day} className="p-2 text-center">
            {day}
          </div>
        ))}
      </div>
    );
  };

  // Calendar grid rendering
  const renderCalendarGrid = () => {
    if (view === "month") {
      // For month view, we need to start from the first day of the week of the month's start
      const monthStart = startOfMonth(currentDate);
      const monthStartDay = getDay(monthStart); // 0 = Sunday, 1 = Monday, etc.

      // Create an array to represent all cells in the calendar grid
      const calendarDays = [];

      // Add days from previous month to fill the first row
      for (let i = 0; i < monthStartDay; i++) {
        const day = addDays(monthStart, -monthStartDay + i);
        calendarDays.push(day);
      }

      // Add all days of the current month
      const daysInMonth = getDaysToDisplay();
      daysInMonth.forEach((day) => {
        calendarDays.push(day);
      });

      // Add days from next month to complete the grid (6 rows x 7 columns = 42 cells)
      const daysToAdd = 42 - calendarDays.length;
      for (let i = 1; i <= daysToAdd; i++) {
        const day = addDays(daysInMonth[daysInMonth.length - 1], i);
        calendarDays.push(day);
      }

      return (
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);

            return (
              <DayCell
                key={index}
                day={day}
                events={dayEvents}
                isCurrentMonth={isCurrentMonth}
                isCurrentDay={isCurrentDay}
                view={view}
                onEventClick={onEventClick}
              />
            );
          })}
        </div>
      );
    } else {
      // For week view
      const days = getDaysToDisplay();
      return (
        <div className="grid grid-cols-7">
          {days.map((day, index) => {
            const dayEvents = getEventsForDay(day);
            const isCurrentMonth = isSameMonth(day, currentDate);
            const isCurrentDay = isToday(day);

            return (
              <DayCell
                key={index}
                day={day}
                events={dayEvents}
                isCurrentMonth={isCurrentMonth}
                isCurrentDay={isCurrentDay}
                view={view}
                onEventClick={onEventClick}
              />
            );
          })}
        </div>
      );
    }
  };

  return (
    <>
      {renderHeaderRow()}
      {renderCalendarGrid()}
    </>
  );
};

export default CalendarGrid;
